import { GetProcessesResponseBodyType } from "@intelligems/ig-types";
import * as Sentry from "@sentry/react";
import { useDispatch, useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { setProcesses } from "@src/store/slices/processes";
import {
  QueryObserverOptions,
  useIsMutating,
  useQuery,
} from "@tanstack/react-query";

import { api } from "../api";
import { useGetOrg } from "../organizations/get-organization";
import { processesKeys } from "../queryKeys";

/**
 * The wire payload is an untrusted boundary. `GetProcessesResponseBodySchema`
 * declares `processes` as always present and consumers read it straight off the
 * query data to call array methods on it, so this fetcher is the layer that
 * makes the declared contract actually hold. Item shape is already guaranteed
 * by the backend, which strict-parses the same shared schema before responding.
 */
export const getProcesses = async (
  reportContractViolation: (error: Error) => void
): Promise<GetProcessesResponseBodyType> => {
  const resp = await api.get<Partial<GetProcessesResponseBodyType> | null>(
    `/v2/organizations/processes`
  );
  const processes = resp.data?.processes;

  if (!Array.isArray(processes)) {
    reportContractViolation(
      new Error(
        "GET /v2/organizations/processes responded without a process list"
      )
    );
    return { processes: [] };
  }

  return { processes };
};

export const useGetProcesses = (
  options?: QueryObserverOptions<GetProcessesResponseBodyType, Error>
) => {
  const org = useGetOrg();
  const dispatch = useDispatch();
  return useQuery<GetProcessesResponseBodyType, Error>({
    queryKey: processesKeys.one(org.data.org.id),
    queryFn: async () => {
      const response = await getProcesses(Sentry.captureException);
      dispatch(setProcesses(response.processes));
      return response;
    },
    refetchInterval: 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    ...options,
  });
};

export const useProcessById = (id: string | undefined) => {
  const processes = useGetProcesses();
  if (!id) return undefined;
  return processes.data?.processes.find((p) => p.id === id);
};

export const useIsExperienceSaving = () => {
  const experience = useSelector(selectedExperience);
  const process = useProcessById(experience?.id);
  const isPutExperienceMutating = useIsMutating({
    mutationKey: ["mutatePutExperience"],
  });
  if (isPutExperienceMutating) {
    return true;
  }
  return process !== undefined;
};
